import type { BrandAsset, GetBrandDTO } from "@nlc/shared-types";

import { DownloadIcon, FolderIcon, Link2Icon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { useDownloadFile } from "@/components/file-uploader/hooks/useDownloadFile";
import { MediaPreviewModalProvider } from "@/components/media-preview-modal/MediaPreviewModalProvider";
import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal";
import { useDownloadBulkAttachments } from "@/features/request/edit/hooks/useDownloadBulkAttachments";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

import { SectionHeader } from "../SectionHeader";

type AssetsTabProps = {
  brand: Pick<GetBrandDTO, "assets">;
};

export function AssetsTab(props: AssetsTabProps) {
  return (
    <MediaPreviewModalProvider>
      <AssetsTabContent {...props} />
    </MediaPreviewModalProvider>
  );
}

function AssetsTabContent({ brand }: AssetsTabProps) {
  const { assets } = brand;
  const { openModal, setMediaItems } = useMediaPreviewModal();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const { mutate: downloadBulk } = useDownloadBulkAttachments();

  useEffect(() => {
    setMediaItems(
      assets
        .filter((asset) => asset.mime !== "link")
        .map((asset) => ({
          extension: getExtensionFromFilename(asset.name),
          id: String(asset.id),
          name: asset.name,
          url: asset.url,
        })),
    );
  }, [assets, setMediaItems]);

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const handleDownloadAll = () => {
    if (assets.length > 0) downloadBulk(assets.map((asset) => asset.id));
  };

  const handleDownloadSelected = () => {
    if (selectedIds.size > 0) downloadBulk([...selectedIds]);
  };

  return (
    <div className="flex flex-col gap-xl">
      <SectionHeader Icon={FolderIcon} title="Assets" />

      {assets.length === 0 ? (
        <p className="py-xl text-center text-sm text-text-quaternary">
          No assets available
        </p>
      ) : (
        <div className="flex flex-col gap-md">
          <div className="flex gap-sm">
            <Button
              color="gray"
              onPress={handleDownloadAll}
              size="sm"
              variant="tertiary"
            >
              Download all
            </Button>
            <Button
              color="gray"
              isDisabled={selectedIds.size === 0}
              onPress={handleDownloadSelected}
              size="sm"
              variant="tertiary"
            >
              Download selected
            </Button>
          </div>

          <div className="flex flex-col gap-md">
            {assets.map((asset) => (
              <AssetItem
                asset={asset}
                isSelected={selectedIds.has(asset.id)}
                key={asset.id}
                onPreview={openModal}
                onToggle={toggleSelection}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type AssetItemProps = {
  asset: BrandAsset;
  isSelected: boolean;
  onPreview: (id: string) => void;
  onToggle: (id: number) => void;
};

function AssetItem({ asset, isSelected, onPreview, onToggle }: AssetItemProps) {
  return (
    <div className="flex items-center gap-md rounded-xl border border-border-secondary bg-bg-primary px-xl py-lg">
      <Checkbox
        isSelected={isSelected}
        onChange={() => onToggle(asset.id)}
        size="md"
      />
      {asset.mime === "link" ? (
        <LinkAsset asset={asset} />
      ) : (
        <FileAsset asset={asset} onPreview={onPreview} />
      )}
    </div>
  );
}

function LinkAsset({ asset }: { asset: BrandAsset }) {
  return (
    <>
      <a
        className="min-w-0 flex-1 cursor-pointer truncate text-sm font-medium text-text-secondary hover:underline"
        href={asset.url}
        rel="noopener noreferrer"
        target="_blank"
        title={asset.name}
      >
        {asset.name}
      </a>
      <Button asChild color="gray" isIconOnly size="sm" variant="tertiary">
        <a
          aria-label={`Open ${asset.name}`}
          href={asset.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Link2Icon className="text-button-tertiary-color-fg" />
        </a>
      </Button>
    </>
  );
}

function FileAsset({
  asset,
  onPreview,
}: {
  asset: BrandAsset;
  onPreview: (id: string) => void;
}) {
  const { mutate: downloadFile } = useDownloadFile();

  return (
    <>
      <button
        className="min-w-0 flex-1 cursor-pointer truncate text-left text-sm font-medium text-text-secondary"
        onClick={() => onPreview(String(asset.id))}
        title={asset.name}
        type="button"
      >
        {asset.name}
      </button>
      <Button
        aria-label={`Download ${asset.name}`}
        color="gray"
        isIconOnly
        onPress={() => downloadFile(asset.id)}
        size="sm"
        variant="tertiary"
      >
        <DownloadIcon className="text-button-tertiary-color-fg" />
      </Button>
    </>
  );
}
